import {
  Component,
  EventEmitter,
  ElementRef,
  HostListener,
  Input,
  Output,
  inject,
  ChangeDetectionStrategy,
  ViewChild,
  OnDestroy,
} from '@angular/core';

import { CommonModule } from '@angular/common';

import { DROPDOWN_PLAIN } from '../../constants/cash-reconciliation.constants';

export interface CustomDropdownOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-custom-dropdown',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './custom-dropdown.component.html',
  styleUrls: ['./custom-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomDropdownComponent implements OnDestroy {
  private readonly el = inject(ElementRef<HTMLElement>);

  @ViewChild('triggerBtn', { static: true })
  triggerBtn!: ElementRef<HTMLButtonElement>;

  @ViewChild('panelRef')
  panelRef?: ElementRef<HTMLDivElement>;

  @Input() variant: 'status' | 'plain' = 'plain';

  @Input() options: CustomDropdownOption[] = [];

  @Input() value = '';

  @Input() triggerStyle: { background: string; color: string } = {
    background: DROPDOWN_PLAIN.bg,
    color: DROPDOWN_PLAIN.color,
  };

  @Input() disabled = false;

  @Input() controlled = false;

  @Input() isOpen = false;

  @Output() isOpenChange = new EventEmitter<boolean>();

  @Output() valueChange = new EventEmitter<string>();

  open = false;

  panelDirection: 'up' | 'down' = 'down';

  private readonly PANEL_HEIGHT = 320;

  private readonly GAP = 4;

  ngOnDestroy(): void {
    this.removeListeners();
  }

  get panelOpen(): boolean {
    return this.controlled ? this.isOpen : this.open;
  }

  get label(): string {
    return this.options.find(o => o.value === this.value)?.label ?? this.value;
  }

  get triggerPlainStyle(): Record<string, string> {
    return {
      background: DROPDOWN_PLAIN.bg,
      color: DROPDOWN_PLAIN.color,
      border: DROPDOWN_PLAIN.border,
    };
  }

  get triggerPillStyle(): Record<string, string> {
    return {
      background: this.triggerStyle.background,
      color: this.triggerStyle.color,
    };
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.panelOpen) {
      return;
    }

    const target = event.target as Node | null;

    if (target && this.el.nativeElement.contains(target)) {
      return;
    }

    this.closePanel();
  }

  toggle(event: Event): void {
    event.stopPropagation();

    if (this.disabled) {
      return;
    }

    const next = !this.panelOpen;

    if (this.controlled) {
      this.isOpenChange.emit(next);
    } else {
      this.open = next;
    }

    if (next) {
      this.addListeners();

      requestAnimationFrame(() => {
        this.updatePanelPosition();
      });
    } else {
      this.removeListeners();
    }
  }

  select(option: CustomDropdownOption, event: Event): void {
    event.stopPropagation();

    // Don't mutate `value` locally: the parent owns it and rewrites the input
    // once the update persists. Self-assigning would leave the trigger showing
    // an unpersisted selection if the request fails.
    if (option.value !== this.value) {
      this.valueChange.emit(option.value);
    }

    this.closePanel();
  }

  private closePanel(): void {
    this.removeListeners();

    if (this.controlled) {
      this.isOpenChange.emit(false);
    } else {
      this.open = false;
    }
  }

  private updatePanelPosition(): void {
    if (!this.triggerBtn || !this.panelRef) {
      return;
    }

    const trigger = this.triggerBtn.nativeElement;
    const panel = this.panelRef.nativeElement;

    const rect = trigger.getBoundingClientRect();

    const panelHeight = panel.offsetHeight || this.PANEL_HEIGHT;

    const viewportHeight = window.innerHeight;

    const spaceAbove = rect.top;
    const spaceBelow = viewportHeight - rect.bottom;

    const openUp = spaceBelow < panelHeight && spaceAbove > spaceBelow;

    let top = openUp ? rect.top - panelHeight : rect.bottom;

    // Prevent viewport overflow
    const padding = 8;

    top = Math.max(padding, Math.min(top, viewportHeight - panelHeight - padding));

    // Optional: allow internal scroll when space is limited
    const availableHeight = openUp ? spaceAbove - padding : spaceBelow - padding;

    panel.style.maxHeight = `${availableHeight}px`;
    panel.style.overflowY = 'auto';

    panel.style.width = `${rect.width}px`;
    panel.style.transform = `translate3d(${rect.left}px, ${top}px, 0)`;
  }

  private readonly repositionHandler = () => {
    if (!this.panelOpen) {
      return;
    }

    this.updatePanelPosition();
  };

  private addListeners(): void {
    window.addEventListener('scroll', this.repositionHandler, true);

    window.addEventListener('wheel', this.repositionHandler, true);

    window.addEventListener('touchmove', this.repositionHandler, true);

    window.addEventListener('resize', this.repositionHandler);
  }

  private removeListeners(): void {
    window.removeEventListener('scroll', this.repositionHandler, true);

    window.removeEventListener('wheel', this.repositionHandler, true);

    window.removeEventListener('touchmove', this.repositionHandler, true);

    window.removeEventListener('resize', this.repositionHandler);
  }
}
